import { addToast, Button, Modal, TextAreaInput } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useValidations } from '../../../hooks/useValidations';
import { TranslationsKeys } from '../../../locales/translations';
import { createStudentActionRecord } from '../actions/studentActions';

export type StudentActionRecordCreateModalMethods = {
  open: (actionId: string | number) => void;
  close: () => void;
};

type StudentActionRecordCreateModalProps = {
  studentId: number | string;
  onSave?: () => void;
};

type FormFields = {
  notes: string;
};

const DEFAULT_VALUES: FormFields = {
  notes: '',
};

const StudentActionRecordCreateModal = (
  { studentId, onSave }: StudentActionRecordCreateModalProps,
  ref: Ref<StudentActionRecordCreateModalMethods>,
) => {
  const { msgValidations } = useValidations();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_OBSERVATIONS,
  );
  const [visible, setVisible] = useState(false);
  const [actionId, setActionId] = useState<string | number>();
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const [notesValue] = watch(['notes']);

  const notesCount = useMemo(() => {
    return notesValue?.length ?? 0;
  }, [notesValue]);

  const handleOpen = useCallback(
    (id: string | number) => {
      setActionId(id);
      setVisible(true);
      reset(DEFAULT_VALUES);
    },
    [reset],
  );

  const handleClose = useCallback(() => {
    setVisible(false);
    setActionId(undefined);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    async (data: FormFields) => {
      if (loading || !actionId) {
        return;
      }

      setLoading(true);
      try {
        const response = await createStudentActionRecord(studentId, actionId, {
          notes: data.notes,
        });

        if (!response.error) {
          addToast({
            color: 'success',
            text: t(`studentActionsRecordsModal.success`),
          });
          onSave?.();
          setVisible(false);
        } else {
          addToast({
            color: 'danger',
            text: t(`studentActionsRecordsModal.failed`),
          });
        }
      } catch (error) {
        console.error('Error creating student action record:', error);
        addToast({
          color: 'danger',
          text: t(`studentActionsRecordsModal.failed`),
        });
      } finally {
        setLoading(false);
      }
    },
    [loading, actionId, studentId, onSave, t],
  );

  if (!visible) {
    return <></>;
  }

  return (
    <Modal isOpen={true} size="lg" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center">
              {t(`studentActionsRecordsModal.title`)}
            </h2>
          </Col>
        </Row>
        <Row className="my-5">
          <Col xs={12} md={12}>
            <TextAreaInput
              control={control}
              name="notes"
              label={t(`studentActionsRecordsModal.notes`)}
              rules={{
                required: msgValidations.required,
              }}
              disabled={loading}
              maxLength={200}
            />
            <p>{`${notesCount} / 200`}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.cancel')}
              type="button"
              fullwidth
              outlined
              onClick={handleClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.save')}
              type="button"
              fullwidth
              onClick={handleSubmit(handleSave)}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(StudentActionRecordCreateModal);
